import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog.js";
import { Button } from "./ui/button.js";
import { Building2, CheckCircle2 } from "lucide-react";

export interface EmisorInfo {
  rut: string;
  razon_social: string;
}

export default function EmisorSelectorModal({
  open,
  emisores,
  loading,
  onClose,
  onSelect,
}: {
  open: boolean;
  emisores: EmisorInfo[];
  loading: boolean;
  onClose: () => void;
  onSelect: (emisorRut: string) => void;
}) {
  const [selected, setSelected] = useState<string>("");

  const handleSubmit = () => {
    if (emisores.length === 1) {
      onSelect(emisores[0].rut);
    } else if (selected) {
      onSelect(selected);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Seleccionar empresa emisora</DialogTitle>
          <DialogDescription>
            {loading
              ? "Consultando empresas disponibles en el SII..."
              : emisores.length === 1
                ? "Tus boletas serán emitidas por la siguiente empresa:"
                : "Selecciona con qué empresa deseas emitir las boletas electrónicas."}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        ) : emisores.length === 1 ? (
          <div className="flex items-center gap-3 rounded-md border border-primary/30 bg-primary/5 p-4">
            <Building2 className="h-5 w-5 text-primary shrink-0" />
            <div>
              <p className="text-sm font-medium">{emisores[0].razon_social}</p>
              <p className="text-xs text-muted-foreground">RUT: {emisores[0].rut}</p>
            </div>
            <CheckCircle2 className="h-5 w-5 text-primary ml-auto shrink-0" />
          </div>
        ) : (
          <div className="space-y-2">
            {emisores.map((emisor) => (
              <label
                key={emisor.rut}
                className={`flex items-center gap-3 rounded-md border p-3 cursor-pointer transition-colors ${
                  selected === emisor.rut ? "border-primary bg-primary/5" : "border-border hover:bg-muted/50"
                }`}
              >
                <input
                  type="radio"
                  name="emisor"
                  value={emisor.rut}
                  checked={selected === emisor.rut}
                  onChange={() => setSelected(emisor.rut)}
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <p className="text-sm font-medium">{emisor.razon_social}</p>
                  <p className="text-xs text-muted-foreground">RUT: {emisor.rut}</p>
                </div>
              </label>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            type="button"
            disabled={loading || (emisores.length > 1 && !selected)}
            onClick={handleSubmit}
          >
            Confirmar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
